import { SerpQueryParam } from '@jusbrasil-web/shared-search-filters'
import { encodeQuery, getJusbrasilBaseURL } from '@jusbrasil-web/shared-utils-url'

import { getHappyCanonical } from './get-happy-canonical'

export interface QueryParamProps {
  q?: string
  p?: string
}

export interface BuildCanonicalSERPUrlProps {
  path?: string
  queryParams?: Record<string, string>
  softNavigation?: boolean
  includeAllQueryParams?: boolean
}

/**
 * @description Function that builds SERP canonical URL.
 * @param path Path of the canonical URL.
 * @param queryParams Query params of the canonical URL.
 */
export const buildCanonicalSERPUrl = ({
  path,
  queryParams = {},
  softNavigation = false,
  includeAllQueryParams = false,
}: BuildCanonicalSERPUrlProps): string => {
  const clonedQueryParams = { ...queryParams }
  if (!path || !clonedQueryParams || !clonedQueryParams[SerpQueryParam.Query]) {
    throw new Error('Invalid arguments')
  }
  const query = clonedQueryParams[SerpQueryParam.Query]

  const happyCanonical = getHappyCanonical(
    `${path}?${SerpQueryParam.Query}=${query}`.toLocaleLowerCase()
  )
  if (happyCanonical) {
    return happyCanonical
  }

  const baseURL = getJusbrasilBaseURL()
  const encodedSearchQuery = encodeQuery(query)
  const page = getPaginationFromQueryParams(clonedQueryParams)
  const url = new URL(`${baseURL}${path}`)
  delete clonedQueryParams[SerpQueryParam.Query]
  delete clonedQueryParams[SerpQueryParam.Page]
  delete clonedQueryParams[SerpQueryParam.Court]

  const params = includeAllQueryParams
    ? new URLSearchParams(clonedQueryParams)
    : new URLSearchParams()
  if (page) {
    params.set(SerpQueryParam.Page, page.toString())
  }

  url.search =
    `${SerpQueryParam.Query}=${encodedSearchQuery}` + (params.size ? `&${params.toString()}` : '')
  const fullURL = url.toString()

  return softNavigation ? fullURL.replace(baseURL, '') : fullURL
}

function getPaginationFromQueryParams(queryParams: QueryParamProps = {}): number | null {
  if (!(queryParams && queryParams.p)) return null
  const page = Number(queryParams.p)
  if (!(Number.isSafeInteger(page) && page > 1)) return null
  return page
}
